import React, { useEffect, useState } from "react";
import { Download, Loader2, ExternalLink } from "lucide-react";
import api from "../lib/api";
import * as C from "../lib/crypto";
import { isProtonDrive } from "../lib/proton";

// File name and type live in the item body, encrypted with the Beacon key when the lesson is private.
const useFileMeta = (it, encKey) => {
  const [meta, setMeta] = useState(() => (it.enc ? null : parseMeta(it.body)));
  useEffect(() => {
    if (!it.enc || !it.body_iv || !encKey) return;
    C.decryptText(encKey, it.body, it.body_iv).then((p) => setMeta(parseMeta(p))).catch(() => {});
  }, [it.enc, it.body, it.body_iv, encKey]);
  return { name: meta?.name || it.file_name, mime: meta?.mime || it.file_mime };
};
const parseMeta = (raw) => { try { return JSON.parse(raw || "null"); } catch { return null; } };

// Downloads a lesson file (PDF, worksheet), decrypting it on the device when the lesson is private.
export const FileDownload = ({ it, encKey, accent, compact }) => {
  const [busy, setBusy] = useState(false);
  const meta = useFileMeta(it, encKey);
  if (!it.media) return null;
  const name = meta.name || `${it.title || "download"}`;
  const get = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/files/${it.media}`, { responseType: "blob" });
      const mime = meta.mime || "application/octet-stream";
      let blob = r.data;
      if (it.enc && encKey) {
        const buf = await r.data.arrayBuffer();
        blob = C.isChunkedMedia(buf) ? await C.decryptChunkedToBlob(encKey, buf, mime) : new Blob([await C.decryptBytes(encKey, buf, it.media_iv)], { type: mime });
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { window.alert("Couldn't download this file, please try again."); }
    finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={get} disabled={busy} data-testid={`file-download-${it.id}`}
      className={`inline-flex items-center gap-1.5 border font-body text-xs disabled:opacity-50 hover:bg-white/5 transition-colors ${compact ? "px-2 py-1" : "px-3 py-1.5 mt-1"}`} style={{ borderColor: `${accent}88`, color: accent }}>
      {busy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} {name}
    </button>
  );
};

// Every download and link in a course, gathered in one place.
export const ResourcesShelf = ({ section, encKey, accent, visible = () => true }) => {
  const res = section.items.filter((it) => !it.locked && visible(it) && ((it.type === "file" && (it.media || it.link_url)) || (it.type === "link" && it.link_url)));
  if (!res.length) return null;
  return (
    <details className="mb-3 border border-white/10 px-3 py-2" data-testid={`resources-shelf-${section.id}`}>
      <summary className="cursor-pointer font-body text-xs text-zinc-300">Resources ({res.length})</summary>
      <div className="flex flex-wrap gap-2 mt-2">
        {res.map((it) => it.type === "file" && it.media ? <FileDownload key={it.id} it={it} encKey={encKey} accent={accent} compact />
          : <a key={it.id} href={it.link_url} target="_blank" rel="noopener noreferrer" data-testid={`resource-link-${it.id}`} className="inline-flex items-center gap-1.5 border border-white/15 px-2 py-1 font-body text-xs text-zinc-300 hover:bg-white/5"><ExternalLink size={12} />{it.link_label || it.title || "Link"}{isProtonDrive(it.link_url) && <span className="text-zinc-500">· Proton Drive, encrypted</span>}</a>)}
      </div>
    </details>
  );
};
